import { useLazyQuery } from '@apollo/client';
import { Button, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_PAGINATION_TABLE_FIELDS,
  POD_ROLES,
  ROUTES,
} from '../../../../common/constants';
import CommonTable from '../../../../components/CommonTable';
import { useTableQueryParams } from '../../../../hooks/useTableQueryParams';
import { EMPLOYEE_ASSIGNMENTS } from '../graphql/Queries';

const getPodRoleLabel = (value) =>
  POD_ROLES.find((r) => r.value === value)?.label || value;

const buildAssignmentsFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
});

function EmployeeAssignments({ employeeId }) {
  const history = useHistory();
  const { params, setParams } = useTableQueryParams({
    namespace: 'assignments',
    fields: DEFAULT_PAGINATION_TABLE_FIELDS,
  });

  const [fetchAssignments, { loading, data }] = useLazyQuery(
    EMPLOYEE_ASSIGNMENTS,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  useEffect(() => {
    if (employeeId) {
      fetchAssignments({
        variables: { employeeId, filters: buildAssignmentsFilter(params) },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, params]);

  const handleTableChange = (paginationConfig) => {
    const { current: currentPage, pageSize } = paginationConfig;
    setParams({
      page: pageSize !== params?.pageSize ? 1 : currentPage,
      pageSize,
    });
  };

  const columns = [
    {
      title: 'Pod',
      key: 'pod',
      render: (_, record) =>
        record.pod ? (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() =>
              history.push(`${ROUTES?.AOS_STREAMS}/${record.pod.id}/detail`, {
                podName: record.pod.name,
              })
            }
          >
            {record.pod.name}
          </Button>
        ) : (
          '-'
        ),
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) =>
        record.pod?.type ? <Tag>{record.pod.type}</Tag> : '-',
    },
    {
      title: 'Role',
      dataIndex: 'podRole',
      key: 'podRole',
      render: (v) => <Tag>{getPodRoleLabel(v)}</Tag>,
    },
    {
      title: 'Allocation',
      dataIndex: 'allocationPct',
      key: 'allocationPct',
      render: (v) => `${v}%`,
    },
    {
      title: 'Primary',
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      render: (v) => (v ? <Tag color="blue">Primary</Tag> : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (v) =>
        v ? (
          <Tag color="error">Inactive</Tag>
        ) : (
          <Tag color="success">Active</Tag>
        ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (v) => (v ? dayjs(v).format(DEFAULT_DATE_FORMAT) : '-'),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (v) => (v ? dayjs(v).format(DEFAULT_DATE_FORMAT) : '-'),
    },
  ];

  return (
    <div className="card-body-wrapper">
      <CommonTable
        size="small"
        bordered
        columns={columns}
        data={data?.employeeAssignments?.assignments || []}
        loadingData={loading}
        onChange={handleTableChange}
        rowKey={(obj) => obj?.id}
        paginationConfig={{
          current: params?.page,
          total: data?.employeeAssignments?.count,
          pageSize: params?.pageSize,
        }}
      />
    </div>
  );
}

export default EmployeeAssignments;
